import { useEffect, useState } from 'react';
import {
  recommendPlan,
  ApiError,
  type MatchInput,
  type MatchResult,
  type RecommendPlanResponse,
} from '../lib/api.js';
import type { Turn } from './Chat.js';

interface Props {
  agent: MatchResult;
  turns: Turn[];
  input: MatchInput;
  onRestart: () => void;
}

type PlanState =
  | { status: 'loading' }
  | { status: 'ready'; plan: RecommendPlanResponse }
  | { status: 'error'; message: string };

/**
 * Closing screen. After the candidate finishes the senpai conversation,
 * we generate a D-COMPASS-branded study-abroad plan grounded in what
 * they actually said, then offer a (demo-only) CTA into a real consult.
 *
 * The "次へ" button is intentionally a stub — it just resets the flow.
 * The point of the screen is to show the candidate (and the demo
 * audience) that the conversation became something concrete they can
 * carry forward, not to actually take a booking.
 */
export function Wrap({ agent, turns, input, onRestart }: Props) {
  const userTurnCount = turns.filter((t) => t.role === 'user').length;
  const [state, setState] = useState<PlanState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    recommendPlan({
      agent_id: agent.agent_id,
      turns: turns.map((t) => ({ role: t.role, content: t.content })),
      ...(input.interests.length ? { interests: input.interests } : {}),
      phase: input.phase,
      ...(input.free_text ? { free_text: input.free_text } : {}),
    })
      .then((plan) => {
        if (!cancelled) setState({ status: 'ready', plan });
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.userMessage
            : err instanceof Error
              ? err.message
              : String(err);
        setState({ status: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [agent.agent_id, turns, input]);

  const handleNext = () => {
    // Intentional stub for the demo: real flow would deep-link into
    // a customised plan editor / consult booking. We don't want to
    // bounce people through a half-built form on stage, so the click
    // just acknowledges and lets them restart.
    window.alert(
      'デモ版のため、ここから先のカスタマイズ・応募画面は省略しています。\n本番ではこのプランを下敷きに、希望に合わせて編集・予約に進める予定です。',
    );
  };

  return (
    <div className="main">
      <div className="container">
        <p className="kicker">D-COMPASS からのご提案</p>
        <h1>{agent.display_name}との対話を、形にしました。</h1>
        <p className="lede">
          {userTurnCount}回のやりとりを踏まえて、あなたに合わせた渡航プランの下書きを用意しました。
        </p>

        {state.status === 'loading' && (
          <div className="panel panel-spacious plan-loading">
            <div className="thinking">
              <span /> <span /> <span />
            </div>
            <p className="muted">
              会話の中で出てきた関心や不安をもとに、プランを組み立てています…
            </p>
          </div>
        )}

        {state.status === 'error' && (
          <div className="panel panel-spacious">
            <h2>プランの生成に時間がかかっています</h2>
            <p className="muted">{state.message}</p>
            <p className="muted">
              恐れ入りますが、もう一度お試しいただくか、対話を1〜2往復だけ追加してから「まとめる」を押してみてください。
            </p>
          </div>
        )}

        {state.status === 'ready' && (
          <article className="plan-card">
            <header className="plan-card-head">
              <h2>{state.plan.headline}</h2>
              <p>{state.plan.summary}</p>
            </header>

            <section className="plan-section">
              <h3>留学中に行うこと</h3>
              <ol className="plan-items">
                {state.plan.items.map((item, i) => (
                  <li key={i} className="plan-item">
                    <div className="plan-item-title">{item.title}</div>
                    <p className="plan-item-description">{item.description}</p>
                    <p className="plan-item-why">
                      <span className="plan-item-why-label">なぜ:</span> {item.why}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            <section className="plan-section">
              <h3>得られる効果</h3>
              <ul className="plan-effects">
                {state.plan.expected_effects.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </section>
          </article>
        )}

        <div className="plan-cta">
          <button
            type="button"
            className="btn"
            onClick={handleNext}
            disabled={state.status !== 'ready'}
          >
            このプランをカスタムして応募に進む
          </button>
          <p className="plan-cta-hint">
            内容は次の画面で自由に編集できます。気になった項目だけ残すことも可能です。
          </p>
        </div>

        <p className="footnote">
          ※ このプロトタイプは進路相談の代わりではありません。決断は人と話して進めてください。
        </p>

        <div className="row row-trailing">
          <button type="button" className="btn-ghost" onClick={onRestart}>
            最初から始める
          </button>
        </div>
      </div>
    </div>
  );
}
